import { notify } from 'src/clients/ApiService';

import { ApiMethods, ApiTags, appAPI } from '.';

export enum RefundStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export type Refund = {
  id: string;
  ref?: string;
  data?: string;
  invoiceId?: string;
  createdAt: string;
  updatedAt: string;
  status: RefundStatus;
  amount: number;
  completedAt?: string;
  failureReason?: string;
};

export interface ListRefundsParams {
  invoiceId: string;
  limit?: number;
  nextToken?: string;
}

interface ListRefundsResponse {
  data: Refund[];
  nextToken?: string;
}

export const refundsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    refundInvoice: build.mutation<Refund, { id: string }>({
      query: ({ id }) => ({
        path: `/invoices/${id}/refund`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: [
        ApiTags.refunds,
        ApiTags.invoices,
        ApiTags.clientInvoices,
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Refund initiated',
            dispatch,
          });
        } catch {
          // The refund modal surfaces mutation errors inline.
        }
      },
    }),
    getRefundsByInvoiceId: build.query<Refund[], ListRefundsParams>({
      query: (params) => {
        const qs = new URLSearchParams();
        qs.set('invoiceId', params.invoiceId);
        if (params.limit != null) qs.set('limit', String(params.limit));
        if (params.nextToken) qs.set('nextToken', params.nextToken);
        return {
          path: `/refunds?${qs.toString()}`,
          method: ApiMethods.get,
          options: {},
        };
      },
      transformResponse: (response: ListRefundsResponse) =>
        (response.data || [])
          .slice()
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
      providesTags: [ApiTags.refunds],
    }),
  }),
});

export const { useRefundInvoiceMutation, useGetRefundsByInvoiceIdQuery } =
  refundsApi;
